/**
 * bleManager.js
 * Generosity(TM) WTR App -- Persistent BLE Connection Manager for the PP02 Smart Cap.
 * Drop into: frontend/src/lib/bleManager.js
 *
 * WHY THIS FILE EXISTS
 * The WTR BTL screen used to OWN the Bluetooth connection in component state.
 * When the user switched tabs (WTR BTL -> My Hydration -> WTR BTL), React
 * unmounted the screen and destroyed the BluetoothDevice reference, so every
 * return showed "Reconnect Bottle". Web Bluetooth cannot recreate a device
 * object without a fresh user tap, so the link was gone for good on each
 * unmount.
 *
 * This module lives at module scope. It is created ONCE per page load and is
 * never unmounted by React. The live GATT link, the poll loop, and the latest
 * telemetry all live here, so navigating between tabs no longer drops the
 * bottle. Screens subscribe to it; they do not own it.
 *
 * A full page reload still requires one tap. No Web Bluetooth link survives a
 * reload -- that is a browser limit, not something this file can change.
 *
 * BLE PROTOCOL, PP02 V05 (Production Partners, 2026-01-16)
 *   Service UUID: 0xFFE0 | Write char: 0xFFE2 (APP -> Device)
 *   Notify char:  0xFFE1 (Device -> APP)
 *   TX packet:    [0x55][CHECKSUM][CMD][0x00][LEN_L][D0..Dn]
 *   RX packet:    [0xAA][CHECKSUM][CMD][LEN_H][LEN_L][D0..Dn]
 *   Checksum:     (CMD + LEN_H + LEN_L + SUM(Data)) & 0xFF
 *
 * CONNECT-PATH HARDENING (2026-07-15, "connected but no transition" incident)
 *   Symptom: the OS reconnects the cap at the link layer (iOS and the PP02
 *   status tile both show "connected"), but the WTR BTL screen stays on the
 *   reconnect/scanning view and never transitions to the live dashboard.
 *   Root cause: the gattserverdisconnected listener was attached BEFORE the
 *   handshake completed, so a spurious Bluefy disconnect during service
 *   discovery spawned a competing resume() while connect() was still awaiting
 *   getPrimaryService. The two GATT sessions raced on the same device and left
 *   status stuck on "scanning". Fixes below:
 *     (1) a module-level `connecting` guard: onDrop never spawns resume() while
 *         a handshake is in flight -- that handshake's own try/catch owns the
 *         cleanup;
 *     (2) the drop listener is attached only AFTER the link is fully
 *         established (characteristics resolved, notifications started);
 *     (3) reconnect failures now surface a visible errMsg instead of dropping
 *         silently to OFFLINE with no explanation;
 *     (4) stale-handle escape (2026-07-15, second pass): when a reconnect via
 *         getDevices() exhausts all retries, the remembered pairing is
 *         cleared so the hero button reverts to Connect Device and the next
 *         tap opens a fresh chooser, instead of looping the same dead handle
 *         forever.
 *
 * V05 SPEC AUDIT (2026-07-15, third pass, against the official PP02 V05
 * protocol document dated 2026-01-16):
 *   Framing, checksum, command set, sensor layout, and all unit decodes
 *   verified against the spec. One gap fixed: sensor status 0x01 (Version,
 *   new in V05) carries the sensor's firmware version in the data bytes and
 *   was being ingested as a live reading. parseInfo now accepts only status
 *   0x00 (Normal) as data and logs version frames once per sensor. Note for
 *   the PP dossier: the spec states 0x05 water temperature transmits in
 *   0.1 degree C units, so the whole-degrees behavior on fw=10 is a firmware
 *   violation of PP's own published V05 spec.
 *
 * EARLY ACTIVATION (2026-07-15, fourth pass): 0x05 Reading Activation now
 * fires the instant the write characteristic resolves, before notification
 * subscribe, in both connect() and resume(). Mitigation for the observed
 * connect-then-drop-in-4s behavior on Bluefy; see the comment at the call
 * site in connect().
 *
 * GATT SERIALIZATION (2026-07-15, fifth pass): the fourth-pass activation
 * write was not awaited, which overlapped it with the next getCharacteristic
 * call. Bluefy rejects concurrent GATT operations with an empty-message
 * error (proven by the 12:43 connection log: TX logged, failure the same
 * second). The write is now awaited in both connect() and resume(), and all
 * failure paths surface the DOMException name when the message is empty.
 *
 * GETDEVICES SINGLE-SHOT (2026-07-15, sixth pass): Bluefy getDevices()
 * handles are conclusively duds (instant empty-error rejections at 11:51
 * and 1:09, vs a completed fresh-chooser handshake in between). The
 * reconnect path now tries the remembered handle exactly once and, on
 * failure, clears it and falls through to the chooser within the same tap.
 * onDrop auto-resume keeps its 3 retries: a just-dropped live device object
 * is real and mid-session recovery deserves them.
 */

import { syncTelemetry } from "./bottleSync";

// ==================================================================
// CONSTANTS
// ==================================================================

const DEV_KEY = "wtr_btl_device"; // remembered pairing { id, name }, survives reloads
const SVC  = "0000ffe0-0000-1000-8000-00805f9b34fb";
const WCHR = "0000ffe2-0000-1000-8000-00805f9b34fb";
const NCHR = "0000ffe1-0000-1000-8000-00805f9b34fb";

const CMD_INFO     = 0x10;
const CMD_ACTIVATE = 0x05;
const CMD_RESET    = 0x03;
const CMD_GOAL     = 0x02;

const POLL_MS         = 2000;
const MAX_HIST        = 60;
const GATT_TIMEOUT_MS = 15000;

// ==================================================================
// PACKET HELPERS
// ==================================================================

function buildPkt(cmd, data = []) {
  const lH = 0x00, lL = data.length & 0xFF;
  const cs  = (cmd + lH + lL + data.reduce((a, b) => a + b, 0)) & 0xFF;
  return new Uint8Array([0x55, cs, cmd, lH, lL, ...data]);
}

function parseInfo(buf) {
  if (!buf || buf.length < 6)           return null;
  if (buf[0] !== 0xAA)                  return null;
  if (buf[2] !== CMD_INFO)              return null;
  const dLen = (buf[3] << 8) | buf[4];
  if (buf.length < 5 + dLen)           return null;
  const d = i => buf[5 + i];

  const sensors    = {};
  const numSensors = Math.floor((dLen - 4) / 3);

  for (let n = 0; n < numSensors; n++) {
    const b      = 4 + n * 3;
    const tRaw   = d(b), vH = d(b + 1), vL = d(b + 2);
    const status = (tRaw >> 5) & 0x07;
    const type   = tRaw & 0x1F;
    const raw    = (vH << 8) | vL;
    // V05 spec, section 3.1: status 0x00 = Normal (data), 0x01 = Version (the
    // data bytes carry the SENSOR'S FIRMWARE VERSION, not a reading), 0x07 =
    // Error. Only Normal frames are sensor data. The old gate skipped only
    // 0x07, so a version frame's number was ingested as a live reading (TDS
    // sensor at fw 100 would record as 100 ppm): fabricated data through the
    // front door. Version frames are logged once per sensor per session so
    // the sensor FW inventory lands in the debug report for the Production
    // Partners dossier, then skipped.
    if (status !== 0x00) {
      if (status === 0x01 && !sensorFwLogged.has(type)) {
        sensorFwLogged.add(type);
        addLog(`sensor 0x${type.toString(16).toUpperCase().padStart(2, "0")} FW version ${raw}`);
      }
      continue;
    }

    let val = raw, unit = "", label = "", key = "";
    switch (type) {
      case 0x01: key="battTemp";   label="Battery Temp";  val=raw-20;               unit="\u00B0C";  break;
      case 0x02: key="tds";        label="TDS";           val=raw;                  unit="ppm"; break;
      case 0x03: key="ph";         label="pH";            val=(raw/100).toFixed(2); unit="pH";  break;
      case 0x04: key="waterLevel"; label="Water Level";   val=raw;                  unit="mm";  break;
      case 0x05: key="waterTemp";  label="Water Temp";    val=(raw/10).toFixed(1);  unit="\u00B0C";  break;
      case 0x06: key="volume";     label="Volume";        val=raw;                  unit="mL";  break;
      case 0x07: key="intakePct";  label="Intake %";      val=raw;                  unit="%";   break;
      case 0x10: key="accX";  label="ACC-X";  unit="raw"; break;
      case 0x11: key="accY";  label="ACC-Y";  unit="raw"; break;
      case 0x12: key="accZ";  label="ACC-Z";  unit="raw"; break;
      case 0x16: key="pitch"; label="Pitch";  unit="\u00B0";   break;
      case 0x17: key="yaw";   label="Yaw";    unit="\u00B0";   break;
      case 0x18: key="roll";  label="Roll";  unit="\u00B0";   break;
      default:
        key = `s${type}`;
        label = `Sensor 0x${type.toString(16).toUpperCase().padStart(2,"0")}`;
        break;
    }
    sensors[key] = { label, val, unit, raw, type, status };
  }

  return {
    state:    d(0),
    charging: !!((d(1) >> 7) & 1),
    batt:     d(1) & 0x7F,
    fw:       ((d(2) << 8) | d(3)),
    sensors,
    ts:       Date.now(),
  };
}

// ==================================================================
// MODULE STATE (survives React unmounts; one instance per page load)
// ==================================================================

let device     = null;   // BluetoothDevice (kept alive across tab switches)
let writeChar  = null;   // 0xFFE2
let notifyChar = null;   // 0xFFE1
let pollTimer  = null;
let rxBuf      = [];      // rolling receive assembly buffer
let intentional = false;  // true only when the user tapped Disconnect
let prevTele    = null;   // last real frame, baseline for intake episode detection
let sensorFwLogged = new Set(); // sensor types whose FW version frame was already logged this session
// True while a connect() or resume() handshake is in flight. onDrop must not
// spawn a competing resume() during this window: the in-flight handshake owns
// the device lifecycle and its own try/catch cleans up on failure. This guard
// is the fix for the 2026-07-15 "connected but no transition" race.
let connecting  = false;

function readRemembered() {
  try { return JSON.parse(localStorage.getItem(DEV_KEY) || "null"); }
  catch (e) { return null; }
}

const s = {
  status:     "idle",   // idle | scanning | connected | error
  errMsg:     "",
  tele:       null,
  hist:       [],
  intake:     0,
  log:        [],
  remembered: readRemembered(),
  deviceId:   null,
  deviceName: null,
};

// ------------------------------------------------------------------
// Pub/sub for useSyncExternalStore. getSnapshot must return the SAME
// object reference between commits, so we rebuild the snapshot only
// when something actually changes.
// ------------------------------------------------------------------
const listeners = new Set();
let snapshot = buildSnapshot();

function buildSnapshot() {
  return {
    status:     s.status,
    errMsg:     s.errMsg,
    tele:       s.tele,
    hist:       s.hist,
    intake:     s.intake,
    log:        s.log,
    remembered: s.remembered,
    deviceId:   s.deviceId,
    deviceName: s.deviceName,
  };
}

function commit() {
  snapshot = buildSnapshot();
  listeners.forEach(fn => fn());
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getSnapshot() {
  return snapshot;
}

// ==================================================================
// INTERNAL HELPERS
// ==================================================================

function setStatus(next) { s.status = next; commit(); }

function addLog(msg, bytes) {
  const hex = bytes
    ? Array.from(bytes).map(b => b.toString(16).padStart(2, "0").toUpperCase()).join(" ")
    : "";
  s.log = [{ t: new Date().toLocaleTimeString(), msg, hex }, ...s.log].slice(0, 200);
  commit();
}

function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(
        `${label} timed out after 15 s. Power-cycle the Smart Cap and try again.`
      )), GATT_TIMEOUT_MS)
    ),
  ]);
}

async function sendInternal(cmd, data = []) {
  if (!writeChar) return;
  const pkt = buildPkt(cmd, data);
  addLog(`\u2192 0x${cmd.toString(16).toUpperCase().padStart(2, "0")}`, pkt);
  try { await writeChar.writeValueWithResponse(pkt); } catch (e) {}
}

// Notification handler. PERMANENT KEEPERS (2026-07-07 debug branch):
//   1. Resync fix: after slicing rxBuf to the 0xAA head, re-read from the NEW
//      buffer, never the stale pre-slice array.
//   2. Assembled frames are logged distinctly ("FRAME") with a checksum verdict.
function onNotify(evt) {
  const val = new Uint8Array(evt.target.value.buffer);
  addLog("\u2190 RX", val);
  rxBuf.push(...val);
  let b = rxBuf;
  const start = b.indexOf(0xAA);
  if (start < 0) { rxBuf = []; return; }
  if (start > 0) { rxBuf = b.slice(start); b = rxBuf; }
  if (b.length < 5) return;
  const tLen = 5 + (b[3] << 8) + b[4];
  if (b.length < tLen) return;
  const frame = new Uint8Array(rxBuf.splice(0, tLen));
  const csCalc = (frame[2] + frame[3] + frame[4] + frame.slice(5).reduce((a, x) => a + x, 0)) & 0xFF;
  const csOk = csCalc === frame[1];
  addLog(csOk ? "\u2190 FRAME cs OK" : `\u2190 FRAME cs FAIL exp ${csCalc.toString(16).toUpperCase().padStart(2, "0")}`, frame);
  if (frame[2] !== CMD_INFO) return;
  const parsed = parseInfo(frame);
  if (!parsed) return;
  // Live-link heartbeat: stamped on every real telemetry frame so other screens
  // (the My Devices card) can tell "connected right now" from "paired".
  try { localStorage.setItem("wtr_btl_link_ts", String(Date.now())); } catch (e) {}
  s.tele = parsed;
  s.hist = [...s.hist.slice(-(MAX_HIST - 1)), { ...parsed, ts: Date.now() }];
  if (parsed.sensors.volume?.raw) {
    s.intake = Math.max(s.intake, parsed.sensors.volume.raw);
  }
  commit();

  // Verified Intake Event sync lives HERE, in the transport, not in a screen.
  // It used to be a WTR BTL screen effect, which meant episode detection only
  // ran while that tab was mounted. Driving it per accepted frame from the
  // manager means drinking episodes keep posting while the user is on any tab.
  // bottleSync gates dropout and tilt garbage, measures episodes endpoint to
  // endpoint, and rejects demo device ids internally; demo frames never reach
  // this manager in any case.
  const prev = prevTele;
  prevTele = parsed;
  if (prev) {
    try {
      syncTelemetry({
        prev,
        cur: parsed,
        deviceId: s.deviceId || s.deviceName || "wtr-btl",
        sourceHubDevice: null,
      });
    } catch (e) {}
  }
}

function startPoll() {
  const poll = async () => {
    try {
      await sendInternal(CMD_ACTIVATE);
      await new Promise(r => setTimeout(r, 120));
      await sendInternal(CMD_INFO);
    } catch (e) {}
  };
  poll();
  pollTimer = setInterval(poll, POLL_MS);
}

function stopPoll() { clearInterval(pollTimer); }

// Drop handler: auto-resume unless the user chose to disconnect.
function onDrop() {
  stopPoll();
  s.tele = null;
  commit();
  // A disconnect that fires while a connect()/resume() handshake is still in
  // flight is OWNED by that handshake's try/catch, which will reject on its
  // next await and clean up. Spawning a competing resume() here was the
  // 2026-07-15 race: two GATT sessions on the same device left status stuck
  // on "scanning" while the OS still showed the cap connected. Do not resume
  // mid-handshake.
  if (connecting) return;
  const dev = device;
  if (intentional || !dev) { setStatus("idle"); return; }
  resume(dev, 1);
}

// Resume: re-establish the SAME device without the chooser. Covers mid-session
// drops (cap sleeping, brief range loss) with 3 retries. A reconnect is always
// a fresh GATT session, so services and notifications are rediscovered.
// viaGetDevices: true when the handle came from navigator.bluetooth.getDevices()
// (the one-tap reconnect path). Those handles can be STALE in a way a
// just-dropped live device object cannot, so final failure on that path
// clears the remembered pairing to break the retry loop (2026-07-15
// stale-handle trap: remembered + getDevices match = the chooser is
// unreachable forever while the handle never connects).
// singleShot: true when the caller wants exactly one attempt and will handle
// the failure itself (the getDevices reconnect path). On failure, resume
// rethrows instead of retrying or writing a final-failure message.
async function resume(dev, attempt = 1, viaGetDevices = false, singleShot = false) {
  // Never run two handshakes at once. If one is already in flight, this call
  // is redundant and would race it.
  if (connecting && attempt === 1) return;
  connecting = true;
  try {
    setStatus("scanning");
    s.errMsg = "";
    commit();
    addLog(`Reconnecting (attempt ${attempt})\u2026`);
    const srv = await withTimeout(dev.gatt.connect(), "GATT reconnection");
    const svc = await withTimeout(srv.getPrimaryService(SVC), "Service discovery");
    writeChar  = await withTimeout(svc.getCharacteristic(WCHR), "Write characteristic");
    // Early activation, AWAITED to serialize the GATT queue: see the comment
    // in connect().
    await sendInternal(CMD_ACTIVATE);
    notifyChar = await withTimeout(svc.getCharacteristic(NCHR), "Notify characteristic");
    await withTimeout(notifyChar.startNotifications(), "Start notifications");
    notifyChar.removeEventListener("characteristicvaluechanged", onNotify);
    notifyChar.addEventListener("characteristicvaluechanged", onNotify);
    dev.addEventListener("gattserverdisconnected", onDrop, { once: true });
    device = dev;
    s.deviceId   = dev.id || "";
    s.deviceName = dev.name || "PP02 Smart Cap";
    connecting = false;
    setStatus("connected");
    addLog("\u2713 Reconnected");
    startPoll();
  } catch (e) {
    if (singleShot) {
      // The caller owns this failure. Clear the in-flight guard and rethrow
      // so reconnect() can fall through to the chooser inside the same tap.
      connecting = false;
      throw e;
    }
    if (attempt < 3 && !intentional) {
      // Keep `connecting` true across the whole retry chain so a drop during
      // the backoff window cannot spawn yet another resume() via onDrop.
      setTimeout(() => resume(dev, attempt + 1, viaGetDevices), 1500 * attempt);
    } else {
      connecting = false;
      // Surface WHY, instead of dropping to OFFLINE silently. Before this the
      // user saw the reconnect view collapse to OFFLINE with no explanation,
      // which read as "connected but stuck".
      if (viaGetDevices) {
        // The handle came from getDevices() and never connected once. It may
        // be stale, and while the remembered pairing exists the hero button
        // routes every tap back through this same handle, so the chooser is
        // unreachable. Clear the pairing: the button reverts to Connect
        // Device and the next tap opens a fresh chooser. Costs one row tap;
        // breaks the loop.
        try { localStorage.removeItem(DEV_KEY); } catch (_) {}
        s.remembered = null;
        s.errMsg = e.message || (e.name ? `${e.name}: could not reconnect. Power-cycle the lid, then tap Connect Device to choose it fresh.` : "Could not reconnect to the Smart Cap. Power-cycle the lid, then tap Connect Device to choose it fresh.");
      } else {
        s.errMsg = e.message || (e.name ? `${e.name}: could not reconnect. Power-cycle the lid and tap Reconnect.` : "Could not reconnect to the Smart Cap. Power-cycle the lid and tap Reconnect.");
      }
      setStatus("idle");
      addLog("\u2717 Auto-reconnect failed");
    }
  }
}

// ==================================================================
// PUBLIC ACTIONS
// ==================================================================

export async function connect() {
  if (!navigator.bluetooth) {
    s.errMsg = "Web Bluetooth unavailable. Use Bluefy on iPhone or iPad, or Chrome on Android or desktop.";
    setStatus("error");
    return;
  }
  connecting = true;
  try {
    // Fresh history, RX buffer, and episode baseline so stats reflect real
    // frames only and the first frame of a new session is baseline, not delta.
    s.hist = [];
    rxBuf = [];
    prevTele = null;
    sensorFwLogged = new Set();
    s.errMsg = "";
    setStatus("scanning");

    // requestDevice MUST run inside the user gesture. Do no awaits before it.
    let dev;
    try {
      dev = await navigator.bluetooth.requestDevice({
        filters: [{ services: [SVC] }],
        optionalServices: [SVC],
      });
    } catch (filterErr) {
      if (filterErr.name === "NotFoundError") { connecting = false; setStatus("idle"); return; }
      addLog("Filtered scan failed, retrying with broad scan\u2026");
      dev = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [SVC],
      });
    }

    device = dev;
    intentional = false;
    s.deviceId   = dev.id || "";
    s.deviceName = dev.name || "PP02 Smart Cap";
    addLog(`Connecting to "${dev.name || "PP02 Smart Cap"}"\u2026`);

    const srv = await withTimeout(dev.gatt.connect(), "GATT connection");
    const svc = await withTimeout(srv.getPrimaryService(SVC), "Service discovery");
    writeChar  = await withTimeout(svc.getCharacteristic(WCHR), "Write characteristic");
    // EARLY ACTIVATION (2026-07-15, four-second-drop hypothesis): V05 added
    // 0x05 Reading Activation. The 2026-07-15 iPhone video shows the link
    // establishing at the OS level and dropping ~4 s later while the app is
    // still mid-handshake, on a charged, advertising cap, on BOTH the fresh
    // chooser and getDevices paths. Working hypothesis: fw=10 runs a watchdog
    // that drops a central that connects but never activates, and Bluefy's
    // slow discovery loses that race (desktop Chrome always won it, which is
    // why March worked). Firing activation the instant the write channel
    // exists takes the notify-subscribe latency out of the watchdog window.
    // AWAITED (2026-07-15 fifth pass, instant-fail incident): the first
    // version fired this write without awaiting it, and Bluefy rejected the
    // very next GATT call (getCharacteristic) with an empty-message error,
    // because GATT operations on a device must be serialized and Bluefy does
    // not queue them the way Chrome does. The 12:43 connection log proved
    // it: TX logged, then failure in the same second. Awaiting serializes
    // the queue; sendInternal contains its own error handling, so a failed
    // activation write cannot kill the handshake, and activation still
    // lands before the subscribe step.
    await sendInternal(CMD_ACTIVATE);
    notifyChar = await withTimeout(svc.getCharacteristic(NCHR), "Notify characteristic");
    await withTimeout(notifyChar.startNotifications(), "Start notifications");
    notifyChar.addEventListener("characteristicvaluechanged", onNotify);

    // Attach the drop handler ONLY now that the link is fully established.
    // Attaching it before gatt.connect() meant a spurious disconnect during
    // service discovery spawned a competing resume() while this handshake was
    // still awaiting, and the two raced, leaving status stuck on "scanning"
    // (2026-07-15 "connected but no transition"). A disconnect DURING the
    // handshake above now simply rejects the in-flight await and lands in the
    // catch below, which cleans up honestly.
    device.addEventListener("gattserverdisconnected", onDrop, { once: true });

    connecting = false;
    setStatus("connected");
    addLog("\u2713 Connected");
    const mem = { id: dev.id || "", name: dev.name || "PP02 Smart Cap" };
    try { localStorage.setItem(DEV_KEY, JSON.stringify(mem)); } catch (e) {}
    s.remembered = mem;
    commit();
    startPoll();
  } catch (e) {
    connecting = false;
    try { if (device?.gatt?.connected) device.gatt.disconnect(); } catch (_) {}
    if (e.name === "NotFoundError") {
      setStatus("idle");
    } else {
      // Empty-message DOMExceptions (Bluefy produces them) still carry a
      // name, and NetworkError vs SecurityError vs NotSupportedError are
      // different diagnoses. Surface the name so the log is never blank on
      // the WHY.
      const detail = e.message || (e.name ? `${e.name} (no message)` : "Connection failed");
      s.errMsg = detail;
      setStatus("error");
      addLog(`\u2717 ${detail}`);
    }
  }
}

export function disconnect() {
  intentional = true;
  connecting = false;
  stopPoll();
  try { if (device?.gatt?.connected) device.gatt.disconnect(); } catch (_) {}
  s.tele = null;
  s.deviceId = null;
  s.deviceName = null;
  setStatus("idle");
}

// One-tap reconnect to the remembered bottle. Uses getDevices() where the
// browser grants persisted access (no chooser). Where it does not (Bluefy
// today), falls back to the chooser pre-filtered to the Smart Cap service,
// which is a single row tap.
export async function reconnect() {
  if (!navigator.bluetooth) { return connect(); }
  try {
    if (s.remembered?.id && navigator.bluetooth.getDevices) {
      setStatus("scanning");
      s.errMsg = "";
      commit();
      const known = await navigator.bluetooth.getDevices();
      const match = known.find(d => d.id === s.remembered.id);
      if (match) {
        intentional = false;
        s.hist = [];
        rxBuf = [];
        prevTele = null;
        commit();
        try {
          // SINGLE ATTEMPT (2026-07-15, sixth pass). Evidence from the 11:51
          // and 1:09 connection logs: handles returned by Bluefy's
          // getDevices() reject gatt.connect() instantly with empty errors,
          // every time, while fresh chooser grants complete the handshake.
          // One attempt, and on failure clear the dud pairing and fall
          // through to the chooser below, still inside the same tap's user
          // activation (the dud dies in under a second). Desktop Chrome,
          // where getDevices actually works, succeeds on this one attempt
          // and keeps its tap-free reconnect.
          await resume(match, 1, true, true);
          return;
        } catch (e) {
          addLog(`getDevices handle failed (${e.name || "no name"}); opening chooser\u2026`);
          try { localStorage.removeItem(DEV_KEY); } catch (_) {}
          s.remembered = null;
          commit();
          // fall through to connect()
        }
      }
    }
  } catch (e) {}
  return connect();
}

export async function send(cmd, data = []) {
  return sendInternal(cmd, data);
}

export async function resetIntake() {
  await sendInternal(CMD_RESET);
  s.intake = 0;
  commit();
}

export async function setGoal(ml) {
  await sendInternal(CMD_GOAL, [(ml >> 24) & 0xFF, (ml >> 16) & 0xFF, (ml >> 8) & 0xFF, ml & 0xFF]);
}

export function cancelScan() {
  connecting = false;
  setStatus("idle");
}
